// Canonical "typical adult" sagittal kinematic reference shapes over the gait
// cycle (0..100%), in CADtomie Gait's estimated-angle conventions:
//   hip:   flexion +, extension −
//   knee:  0 = straight, flexion +
//   ankle: dorsiflexion +, plantarflexion − (relative)
//
// These are reference SHAPES for visual context (a corridor), not patient data
// and not a normative database. Labeled "typical pattern" in the UI.

import type { JointKey } from "./rom";

export interface RefBand {
  mean: number[];
  low: number[];
  high: number[];
}

const N = 101;
const gauss = (p: number, mu: number, sigma: number) =>
  Math.exp(-((p - mu) * (p - mu)) / (2 * sigma * sigma));

function build(fn: (p: number) => number, halfBand: number): RefBand {
  const mean: number[] = [];
  const low: number[] = [];
  const high: number[] = [];
  for (let i = 0; i < N; i++) {
    const p = i / (N - 1);
    const v = fn(p);
    mean.push(v);
    low.push(v - halfBand);
    high.push(v + halfBand);
  }
  return { mean, low, high };
}

// Hip: ~+25° flexed at IC → ~−10° extension at terminal stance → back to +25°.
const hip = build((p) => 7.5 + 17.5 * Math.cos(2 * Math.PI * p), 8);

// Knee: small loading-response flexion (~18° @15%) → big swing flexion (~60° @73%).
const knee = build(
  (p) => 5 + 13 * gauss(p, 0.15, 0.09) + 55 * gauss(p, 0.73, 0.12),
  8,
);

// Ankle (relative): brief plantar dip at loading, dorsiflexion through stance,
// strong plantarflexion at push-off (~62%), neutral in swing.
const ankle = build(
  (p) => -5 * gauss(p, 0.08, 0.05) + 10 * gauss(p, 0.45, 0.13) - 16 * gauss(p, 0.62, 0.06),
  7,
);

export const REFERENCE_CURVES: Record<JointKey, RefBand> = { hip, knee, ankle };

export const JOINT_LABEL: Record<JointKey, string> = {
  hip: "Hip flexion / extension",
  knee: "Knee flexion",
  ankle: "Ankle dorsi / plantarflexion",
};
